import React, { useContext } from 'react';
import { formatTime } from '../constant/utils';
import { ThemeContext } from '../context/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faClipboardCheck, 
  faClock,
  faQuestionCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { getPracticeTestIconBg } from '../utils/themeUtils';

const PracticeTestDialog = ({ isOpen, onClose, onStart, onChoosePlan, testDetails, iconBg }) => {
  const { theme, currentTheme } = useContext(ThemeContext);

  if (!isOpen) return null;

  const { is_custom_test, test_name, test_time, total_time, num_questions, requires_subscription } = testDetails;

  // Helper function to get icon background color
  const getIconBg = () => {
    return iconBg;
  };
  const getCardBackground = () => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;
    return {
      background: `${baseColor}`,
      
    };
  };

  const getInfoCardBackground = () => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    return {
      background: `${baseColor}`,
      
    };
  };
  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  let questions = num_questions;
  let testName = test_name;
  const formattedTimeLimit = formatTime(test_time);
  const formattedTotalTime = formatTime(total_time*60);

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.3,
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      y: 20,
      transition: { duration: 0.2 }
    }
  };

  // Button animations
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      boxShadow: "0px 5px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    },
    tap: { scale: 0.95 }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={overlayVariants}
        className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-50 backdrop-blur-sm"
        
      >
        <motion.div
          variants={modalVariants}
          className="rounded-xl shadow-lg p-6 w-full max-w-md relative"
          style={getCardBackground()}
        >
          {/* Header */}
          <div className="flex items-center mb-4" >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
              style={{ backgroundColor: getIconBg() }}
            >
              <FontAwesomeIcon 
                icon={faClipboardCheck} 
                className="text-white" 
                style={{ color: getTextColor() }}
              />
            </div>
            <h2 
              className="text-xl font-semibold flex-1"
              style={{ color: getTextColor() }}
            >
              {testName}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FontAwesomeIcon 
                icon={faTimes} 
                style={{ color: getTextColor() }}
              />
            </button>
          </div>

          {/* Description */}
          <div className="mb-6">
            <div 
              className="p-4 rounded-lg mb-4"
              style={getInfoCardBackground()}
            >
              <div className="flex items-start">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="mt-1 mr-3"
                  style={{ color: getIconBg() }}
                />
                <p 
                  className="text-gray-700 dark:text-gray-300" 
                  style={{ color: getTextColor() }}
                >
                  {is_custom_test ? 
                    "Your questions are being selected to improve your readiness by presenting questions you haven't encountered before, along with questions from areas where you need further practice. The questions are generated from the domains you have selected." :
                    "This is a preset test that will have the same questions every time you take the test. Take the test more often to see how you are progressing."
                  }
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Test Details */}
              {(is_custom_test && total_time > 0) ? (
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faClock}
                    className="mr-3"
                    style={{ color: getIconBg() }}
                  />
                  <p style={{ color: getTextColor() }}>
                    This test has time limit of{" "}
                    <span className="font-bold">
                      {formattedTotalTime}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <FontAwesomeIcon 
                      icon={faQuestionCircle}
                      className="mr-3"
                      style={{ color: getIconBg() }}
                    />
                    <p style={{ color: getTextColor() }}>
                      This test has{" "}
                      <span className="font-bold">
                        {questions} questions
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center">
                    <FontAwesomeIcon 
                      icon={faClock}
                      className="mr-3"
                      style={{ color: getIconBg() }}
                    />
                    <p style={{ color: getTextColor() }}>
                      Time limit is{" "}
                      <span className="font-bold">
                        {formattedTimeLimit}
                      </span>
                    </p>
                  </div>
                </div>
              )}

              <p 
                className="italic text-sm"
                style={{ color: getTextColor() }}
              >
                Make sure you have time to finish the test. Only by finishing the test will you see your performance and affect your readiness score. To emulate a real test, you will not be able to pause the test.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={onClose}
              className="px-5 py-2 rounded-lg font-medium"
              style={{ 
                backgroundColor: currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark,
                color: getTextColor() 
              }}
            >
              Cancel
            </motion.button>
            
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={onStart}
              className="px-5 py-2 rounded-lg font-medium text-white"
              style={{ 
                backgroundColor: getIconBg(),
                color: getTextColor()
              }}
            >
              Start Test
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PracticeTestDialog;